import { useState, useEffect } from "react";
import { Users, UserCheck, UserX, Clock, TrendingUp, Activity, Upload, GraduationCap } from "lucide-react";
import { api } from "../api/client";
import type { DashboardStats } from "../types";

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await api.getDashboardStats();
        setStats(data);
      } catch (err) {
        setError("Gagal memuatkan data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error || "Tiada data"}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <p className="text-slate-600">Ringkasan kehadiran pelajar</p>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Jumlah Penerima"
          value={stats.total}
          icon={<Users className="w-6 h-6" />}
          color="blue"
        />
        <StatCard
          title="Sudah Daftar"
          value={stats.checkedIn}
          icon={<UserCheck className="w-6 h-6" />}
          color="green"
        />
        <StatCard
          title="Belum Daftar"
          value={stats.notCheckedIn}
          icon={<UserX className="w-6 h-6" />}
          color="amber"
        />
        <StatCard
          title="Peratus"
          value={`${stats.percentage}%`}
          icon={<TrendingUp className="w-6 h-6" />}
          color="purple"
        />
      </div>

      {/* DUN Breakdown */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-slate-400" />
          Mengikut DUN / Kawasan
        </h2>
        <div className="space-y-4">
          <CategoryBar
            label="PERMAS"
            total={stats.dun.permas.total}
            checkedIn={stats.dun.permas.checkedIn}
            color="emerald"
          />
          <CategoryBar
            label="JOHOR JAYA"
            total={stats.dun.johorJaya.total}
            checkedIn={stats.dun.johorJaya.checkedIn}
            color="indigo"
          />
        </div>
      </div>

      {/* Gender & Birth State */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Mengikut Jantina (No. IC)</h2>
          <div className="space-y-4">
            <CategoryBar
              label={stats.gender.lelaki.label}
              total={stats.gender.lelaki.total}
              checkedIn={stats.gender.lelaki.checkedIn}
              color="blue"
            />
            <CategoryBar
              label={stats.gender.perempuan.label}
              total={stats.gender.perempuan.total}
              checkedIn={stats.gender.perempuan.checkedIn}
              color="emerald"
            />
            {stats.gender.tidakDiketahui.total > 0 && (
              <CategoryBar
                label={stats.gender.tidakDiketahui.label}
                total={stats.gender.tidakDiketahui.total}
                checkedIn={stats.gender.tidakDiketahui.checkedIn}
                color="amber"
              />
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Mengikut Negeri Kelahiran (No. IC)</h2>
          {stats.birthStates.length === 0 ? (
            <p className="text-slate-500 text-sm">Tiada data negeri kelahiran.</p>
          ) : (
            <div className="space-y-4">
              {stats.birthStates.map((item, index) => (
                <CategoryBar
                  key={`${item.name}-${index}`}
                  label={item.name}
                  total={item.total}
                  checkedIn={item.checkedIn}
                  color={["indigo", "purple", "blue", "emerald", "amber"][index % 5]}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Attendance */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-slate-400" />
          Kehadiran Terkini
        </h2>
        
        {stats.recentAttendance.length === 0 ? (
          <p className="text-slate-500 text-center py-8">Tiada kehadiran lagi</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Bil</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Nama</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Institusi</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Umur</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">DUN</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Masa</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentAttendance.map((log) => (
                  <tr key={log.id} className="border-b border-slate-100">
                    <td className="py-3 px-4 font-mono text-sm">{log.recipient?.bil || "-"}</td>
                    <td className="py-3 px-4">{log.recipient?.nama || "-"}</td>
                    <td className="py-3 px-4 text-sm">{log.recipient?.institution || "-"}</td>
                    <td className="py-3 px-4 text-sm">{log.recipient?.age ?? "-"}</td>
                    <td className="py-3 px-4 text-sm">{log.recipient?.dun}</td>
                    <td className="py-3 px-4 text-sm text-slate-500">
                      {new Date(log.checkedInAt).toLocaleTimeString("ms-MY", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-slate-400" />
          Ringkasan Muat Naik
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-500">Jumlah Upload</p>
            <p className="text-2xl font-bold text-slate-800">{stats.uploadSummary.totalUploads}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-500">Jumlah Import</p>
            <p className="text-2xl font-bold text-slate-800">{stats.uploadSummary.totalImports}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Rekod No. IC Tidak Sah</h2>
        <p className="text-sm text-slate-600 mb-4">
          Jumlah rekod bermasalah: <span className="font-semibold text-red-600">{stats.invalidIcCount}</span>
        </p>

        {stats.invalidIcCount === 0 ? (
          <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
            Tiada rekod No. IC bermasalah.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600 w-24">Bil</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Nama</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600 w-44">No. IC</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Sebab</th>
                </tr>
              </thead>
              <tbody>
                {stats.invalidIcRecords.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100">
                    <td className="py-3 px-2 text-slate-700 font-mono">{item.bil || "-"}</td>
                    <td className="py-3 px-2 text-slate-800">{item.nama || "-"}</td>
                    <td className="py-3 px-2 text-slate-700 font-mono">{item.noIc || "-"}</td>
                    <td className="py-3 px-2 text-red-600 text-sm">{item.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {stats.invalidIcCount > stats.invalidIcRecords.length && (
              <p className="text-xs text-slate-500 mt-3">
                Menunjukkan {stats.invalidIcRecords.length} rekod pertama daripada {stats.invalidIcCount}.
              </p>
            )}
          </div>
        )}
      </div>

      {/* By Institution - Full width bottom */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-slate-400" />
          Semua Institusi / Universiti
        </h2>
        {stats.institutions.length === 0 ? (
          <p className="text-slate-500 text-sm">Tiada data institusi.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600 w-16">#</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Institusi</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-slate-600 w-36">Jumlah</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-slate-600 w-36">Sudah Daftar</th>
                </tr>
              </thead>
              <tbody>
                {stats.institutions.map((item, index) => (
                  <tr key={`${item.name}-${index}`} className="border-b border-slate-100">
                    <td className="py-3 px-2 text-slate-500">{index + 1}</td>
                    <td className="py-3 px-2 font-medium text-slate-800">{item.name}</td>
                    <td className="py-3 px-2 text-right text-slate-700">{item.total}</td>
                    <td className="py-3 px-2 text-right text-slate-700">{item.checkedIn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }: { title: string; value: number | string; icon: React.ReactNode; color: string }) {
  const colors: Record<string, string> = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function CategoryBar({ label, total, checkedIn, color }: { label: string; total: number; checkedIn: number; color: string }) {
  const percentage = total > 0 ? Math.round((checkedIn / total) * 100) : 0;
  
  const colors: Record<string, string> = {
    rose: "bg-rose-500",
    blue: "bg-blue-500",
    emerald: "bg-emerald-500",
    indigo: "bg-indigo-500",
    amber: "bg-amber-500",
    purple: "bg-purple-500",
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-sm text-slate-500">{checkedIn} / {total}</span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colors[color]} rounded-full transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
